import { useState } from "react";
import { Phone, MessageCircle, MapPin, Mail, ArrowRight } from "lucide-react";
import {
    PHONE_NUMBER_DISPLAY,
    PHONE_NUMBER_TEL,
    SERVICE_AREAS,
    buildWhatsAppLink,
} from "../lib/contact";

export const Contact = () => {
    const [form, setForm] = useState({ name: "", phone: "", requirement: "" });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Please enter your name.";
        const phoneDigits = form.phone.replace(/\D/g, "");
        if (phoneDigits.length < 10) e.phone = "Enter a valid phone number.";
        if (!form.requirement.trim())
            e.requirement = "Tell us briefly what you need.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        const message = `Hi Surya Crane Service,\n\nName: ${form.name}\nPhone: ${form.phone}\nRequirement: ${form.requirement}\n\nPlease share details.`;
        const url = buildWhatsAppLink(message);
        // Open WhatsApp with the prefilled message
        window.open(url, "_blank", "noopener,noreferrer");
        setSubmitting(false);
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 lg:py-32 bg-[#0F0F0F]"
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                <div className="reveal mb-14 lg:mb-20 max-w-3xl">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="w-10 h-[2px] bg-[#FFC107]" />
                        <span className="font-body text-xs uppercase tracking-[0.32em] text-[#FFC107]">
                            Get In Touch
                        </span>
                    </div>
                    <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.9]">
                        Tell Us The Lift.
                        <br />
                        <span className="text-neutral-500">
                            We&apos;ll Bring The Crane.
                        </span>
                    </h2>
                    <p className="mt-6 font-body text-neutral-400 text-base lg:text-lg max-w-2xl">
                        Drop your details — we usually reply on WhatsApp within{" "}
                        <span className="text-[#FFC107] font-semibold">
                            10 minutes
                        </span>
                        . For urgent jobs, call us directly.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
                    {/* Form */}
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        noValidate
                        className="lg:col-span-7 bg-[#0A0A0A] p-7 md:p-10"
                    >
                        <div className="flex flex-col gap-5">
                            {/* Name */}
                            <div>
                                <label
                                    htmlFor="name"
                                    className="font-body text-[11px] uppercase tracking-[0.22em] text-neutral-400 mb-2 block"
                                >
                                    Your Name
                                </label>
                                <input
                                    id="name"
                                    type="text"
                                    value={form.name}
                                    onChange={update("name")}
                                    placeholder="e.g. Rajesh Patil"
                                    data-testid="contact-input-name"
                                    aria-invalid={!!errors.name}
                                    className="w-full bg-[#171717] border border-[#262626] focus:border-[#FFC107] focus:outline-none focus:ring-1 focus:ring-[#FFC107] text-white font-body px-4 py-3.5 placeholder:text-neutral-600 transition-colors"
                                />
                                {errors.name && (
                                    <p
                                        data-testid="contact-error-name"
                                        className="mt-1.5 text-xs text-red-400 font-body"
                                    >
                                        {errors.name}
                                    </p>
                                )}
                            </div>

                            {/* Phone */}
                            <div>
                                <label
                                    htmlFor="phone"
                                    className="font-body text-[11px] uppercase tracking-[0.22em] text-neutral-400 mb-2 block"
                                >
                                    Phone Number
                                </label>
                                <input
                                    id="phone"
                                    type="tel"
                                    inputMode="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    placeholder="+91 98xxxxxxxx"
                                    data-testid="contact-input-phone"
                                    aria-invalid={!!errors.phone}
                                    className="w-full bg-[#171717] border border-[#262626] focus:border-[#FFC107] focus:outline-none focus:ring-1 focus:ring-[#FFC107] text-white font-body px-4 py-3.5 placeholder:text-neutral-600 transition-colors"
                                />
                                {errors.phone && (
                                    <p
                                        data-testid="contact-error-phone"
                                        className="mt-1.5 text-xs text-red-400 font-body"
                                    >
                                        {errors.phone}
                                    </p>
                                )}
                            </div>

                            {/* Requirement */}
                            <div>
                                <label
                                    htmlFor="requirement"
                                    className="font-body text-[11px] uppercase tracking-[0.22em] text-neutral-400 mb-2 block"
                                >
                                    Requirement
                                </label>
                                <textarea
                                    id="requirement"
                                    rows={4}
                                    value={form.requirement}
                                    onChange={update("requirement")}
                                    placeholder="Crane capacity, location, dates, scope of work…"
                                    data-testid="contact-input-requirement"
                                    aria-invalid={!!errors.requirement}
                                    className="w-full resize-none bg-[#171717] border border-[#262626] focus:border-[#FFC107] focus:outline-none focus:ring-1 focus:ring-[#FFC107] text-white font-body px-4 py-3.5 placeholder:text-neutral-600 transition-colors"
                                />
                                {errors.requirement && (
                                    <p
                                        data-testid="contact-error-requirement"
                                        className="mt-1.5 text-xs text-red-400 font-body"
                                    >
                                        {errors.requirement}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit-button"
                                className="group inline-flex items-center justify-center gap-3 bg-[#FFC107] hover:bg-[#E0A800] disabled:opacity-60 text-black font-body font-bold text-sm uppercase tracking-[0.18em] px-6 py-4 transition-all duration-200 hover:-translate-y-0.5 focus-ring"
                            >
                                <MessageCircle size={18} strokeWidth={2.5} />
                                Send via WhatsApp
                                <ArrowRight
                                    size={16}
                                    className="transition-transform duration-200 group-hover:translate-x-1"
                                />
                            </button>

                            <p className="font-body text-xs text-neutral-500">
                                By submitting, your details will open a
                                pre-filled WhatsApp chat to our team. No spam.
                            </p>
                        </div>
                    </form>

                    {/* Details */}
                    <aside
                        data-testid="contact-details"
                        className="lg:col-span-5 bg-[#FFC107] text-black p-7 md:p-10 flex flex-col gap-8"
                    >
                        <div>
                            <div className="font-body text-[11px] uppercase tracking-[0.32em] text-black/70 mb-2">
                                Direct Line · 24/7
                            </div>
                            <a
                                href={`tel:${PHONE_NUMBER_TEL}`}
                                data-testid="contact-phone-link"
                                className="font-display text-4xl md:text-5xl text-black leading-tight tracking-wide block hover:underline"
                            >
                                {PHONE_NUMBER_DISPLAY}
                            </a>
                        </div>

                        <div className="flex flex-col gap-3">
                            <a
                                href={buildWhatsAppLink()}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-whatsapp-link"
                                className="inline-flex items-center justify-center gap-2 bg-black text-[#FFC107] hover:bg-neutral-900 font-body font-bold text-sm uppercase tracking-[0.18em] px-5 py-4 transition-colors"
                            >
                                <MessageCircle size={18} strokeWidth={2.5} />
                                Chat on WhatsApp
                            </a>
                            <a
                                href={`tel:${PHONE_NUMBER_TEL}`}
                                data-testid="contact-call-link"
                                className="inline-flex items-center justify-center gap-2 bg-transparent border border-black/30 hover:border-black hover:bg-black/5 text-black font-body font-bold text-sm uppercase tracking-[0.18em] px-5 py-4 transition-colors"
                            >
                                <Phone size={16} strokeWidth={2.5} />
                                Call {PHONE_NUMBER_DISPLAY}
                            </a>
                        </div>

                        <div className="border-t border-black/15 pt-6">
                            <div className="flex items-start gap-3">
                                <MapPin
                                    size={20}
                                    strokeWidth={2}
                                    className="mt-1"
                                />
                                <div>
                                    <div className="font-display text-xl uppercase tracking-wide">
                                        Service Areas
                                    </div>
                                    <p className="font-body text-sm text-black/80 leading-relaxed mt-1">
                                        {SERVICE_AREAS.join(" · ")} &amp;
                                        nearby industrial belts of Maharashtra.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <Mail size={18} strokeWidth={2} className="mt-1" />
                            <div className="font-body text-sm">
                                Prefer email? Send a brief to our team via
                                WhatsApp first — fastest response.
                            </div>
                        </div>
                    </aside>
                </div>

                {/* Map placeholder */}
                <div
                    data-testid="contact-map"
                    className="reveal mt-px relative h-72 md:h-80 border border-white/10 overflow-hidden bg-[#0A0A0A]"
                >
                    <iframe
                        title="Service area map — Mumbai, Thane, Bhiwandi, Wada"
                        src="https://www.openstreetmap.org/export/embed.html?bbox=72.65%2C18.95%2C73.40%2C19.75&layer=mapnik&marker=19.3000%2C73.0500"
                        className="w-full h-full border-0 grayscale contrast-[1.1] opacity-90"
                        loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-[#0A0A0A]/50" />
                    <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur px-4 py-3 border border-white/10">
                        <div className="font-body text-[10px] uppercase tracking-[0.3em] text-[#FFC107] mb-1">
                            Operating Across
                        </div>
                        <div className="font-display text-lg text-white tracking-wide">
                            Mumbai · Thane · Bhiwandi · Wada
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
